import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { message, Upload } from 'antd';
import FetchUtils, { parseJSON } from '../../thunks/fetch_utils';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { connect } from 'react-redux';
import posthog from 'posthog-js';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { previewUploadFile } from './previewUploadFile';

const fetchUtils = new FetchUtils();

const OWUpload = (props) => {
	const {
		uploaderType,
		userType,
		onChange: onChangeProp,
		fileList: fileListProp,
		previewFile: previewFileProp,
		...restProps
	} = props;
	const fileListRef = useRef(fileListProp || []);

	// reset only when there is no upload going on
	useEffect(() => {
		if (fileListProp === undefined) return;
		const hasActiveUploads = fileListRef.current.some((f: any) => f.status === 'uploading');
		if (!hasActiveUploads) {
			fileListRef.current = fileListProp;
		}
	}, [fileListProp]);

	// only change file which is actually changed by comparing uuid
	const handleChange = useCallback(
		(info: any) => {
			const { file } = info;

			if (file.status === undefined) {
				// File rejected by beforeUpload, dont track
				onChangeProp && onChangeProp(info);
				return;
			}

			const currentList = fileListRef.current;
			const existingIndex = currentList.findIndex((f: any) => f.uid === file.uid);

			let updatedList: any[];
			if (file.status === 'removed') {
				updatedList = currentList.filter((f: any) => f.uid !== file.uid);
			} else if (existingIndex >= 0) {
				updatedList = currentList.map((f: any) => (f.uid === file.uid ? { ...f, ...file } : f));
			} else {
				updatedList = [...currentList, file];
			}

			fileListRef.current = updatedList;
			onChangeProp && onChangeProp({ ...info, fileList: updatedList });
		},
		[onChangeProp]
	);

	const UploadComponent = useMemo(() => {
		if (uploaderType === 'dragger') {
			return Upload.Dragger;
		}
		return Upload;
	}, [uploaderType]);
	const userURL = useMemo(() => (userType ? `v1/${userType}` : `portal`), [userType]);
	return (
		<div>
			<UploadComponent
				{...restProps}
				fileList={fileListProp}
				onChange={handleChange}
				previewFile={previewFileProp || previewUploadFile}
				customRequest={({ file, onError, onSuccess, onProgress }) => {
					const fileType = file.type;
					const fileName = file.name;

					const preSignedURLEndpoint = `${getBackendUri()}/api/${userURL}/file/upload/get_presigned_url`;
					fetchUtils
						.post(`${preSignedURLEndpoint}`, {
							data: {
								fileType,
								fileName,
							},
						})
						.then(parseJSON)
						.then(({ json }) => {
							const returnData = json.data;
							const signedRequest = returnData.presignedUrl;

							const uploadStartedAt = Date.now();

							// Use XMLHttpRequest for progress tracking
							const xhr = new XMLHttpRequest();

							// Track upload progress
							xhr.upload.onprogress = (event) => {
								if (event.lengthComputable) {
									const percent = Math.round((event.loaded / event.total) * 100);
									onProgress({ percent });
								}
							};

							xhr.onload = () => {
								const durationMs = Date.now() - uploadStartedAt;
								if (xhr.status >= 200 && xhr.status < 300) {
									posthog.capture('file_upload_completed', {
										source: 'OWUpload',
										file_name: fileName,
										file_type: fileType,
										file_size: nullSafeGet('size', file),
										duration_ms: durationMs,
									});
									const onSuccessPayload = {
										data: returnData,
									};
									onSuccess(onSuccessPayload);
								} else {
									const error = new Error(`Upload failed with status ${xhr.status}`);
									posthog.capture('file_upload_failed', {
										source: 'OWUpload',
										file_name: fileName,
										file_type: fileType,
										file_size: nullSafeGet('size', file),
										duration_ms: durationMs,
										error: error.message,
									});
									onError && onError(error);
									message.error(`${fileName} file upload failed : ${error.message})`);
								}
							};

							xhr.onerror = () => {
								const durationMs = Date.now() - uploadStartedAt;
								const error = new Error('Upload failed');
								posthog.capture('file_upload_failed', {
									source: 'OWUpload',
									file_name: fileName,
									file_type: fileType,
									file_size: nullSafeGet('size', file),
									duration_ms: durationMs,
									error: error.message,
								});
								onError && onError(error);
								message.error(`${fileName} file upload failed : ${error.message})`);
							};

							xhr.onabort = () => {
								const durationMs = Date.now() - uploadStartedAt;
								const error = new Error('Upload cancelled');
								posthog.capture('file_upload_cancelled', {
									source: 'OWUpload',
									file_name: fileName,
									file_type: fileType,
									file_size: nullSafeGet('size', file),
									duration_ms: durationMs,
								});
								onError && onError(error);
							};

							xhr.open('PUT', signedRequest);
							xhr.setRequestHeader('Content-Type', fileType);
							xhr.send(file);
						})
						.catch((error) => {
							onError && onError(error);
							message.error(`File Upload Failed : ${error && JSON.stringify(error)}`);
						});
				}}
			/>
		</div>
	);
};
const mapStateToProps = (state) => {
	return {
		userType: state.session.userType,
	};
};

export default connect(mapStateToProps)(OWUpload);
